//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'
import * as moment from 'moment'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

//#region IMR Imports

import { IMRBaseComponentExtension } from '@imr/controls.angularjs/components/base/extension'

//#endregion

import { DataProviderRequest } from '../../../../../data-provider'
import { TypeHelper, DataTypeGroupComponent, LocalizationHelper, TemplateHelper, ConfigHelper } from '../../../../../helpers'
import { UserSessionService } from '../../../../../services/user-session'
import { DataTypeFormatService, DATA_TYPE_FORMAT_SERVICE_NAME, DataTypeFormatter } from '../../../../../services/data-type'

import { Enums } from "../../../../../models/common"
import { OpDataTypeInGroup } from "../../../../../models/core"
import { MeasureCollection } from "../../../../../models/custom"

import { IIMRChartExtension, IIMRChartExtensionController } from "../../chart.controller"

import { MeasureCollectionChartExtensionChart } from './measure-collection.chart.extension.chart'
import { MeasureCollectionChartExtensionPieChart } from './measure-collection.chart.extension.piechart'


// Public Types

export interface IMeasureCollectionChartExtension { }

export type MeasureCollectionChartExtensionOptions = {
    data?: {
        customMeasurementFilter?: (idLocationList: number[], serialNbrList: number[], idMeterList: number[], idDataTypeList: number[], dateFrom: Date, dateTo: Date) => JQueryPromise<MeasureCollection[]>
    }

    dataTypes?: {
        dataTypeGroupCategory?: Enums.SimaxDataTypeGroupCategory
        dataTypeInGroup?: OpDataTypeInGroup[]
        simaxModuleName?: Enums.SimaxModuleName
    }

    toolbar?: {
        dateFrom?: {
            value?: Date
            visible?: boolean
        },

        dateTo?: {
            value?: Date
            visible?: boolean
        }
    }
}

// Public Types End


// Private Types

export type SeriesInfo = {
    type: Enums.ChartType,
    color: Enums.KnownColorsEnum,
    dataTypeInGroup: OpDataTypeInGroup,
    visible: boolean
}

// Private Types End


export class MeasureCollectionChartExtension extends IMRBaseComponentExtension implements IIMRChartExtension, IMeasureCollectionChartExtension {

    // Consts

    private static get PIE_CHART_TYPES(): Enums.ChartType[] { return [Enums.ChartType.Pie, Enums.ChartType.Doughnut] }

    // Consts End


    // Members 

    protected chart: DevExpress.viz.dxChart
    protected chartController: IIMRChartExtensionController

    protected chartExtension: MeasureCollectionChartExtensionChart
    protected pieChartExension: MeasureCollectionChartExtensionPieChart

    protected pieOnlySeriesInfos: SeriesInfo[]
    protected noPieSeriesInfos: SeriesInfo[]

    protected fromDateBox: DevExpress.ui.dxDateBox
    protected toDateBox: DevExpress.ui.dxDateBox

    protected seriesDataTypeInGroupList: OpDataTypeInGroup[]
    private dataTypeFormatter: DataTypeFormatter

    protected visibleIdDataTypes: number[]
    protected idLocationList: number[]
    protected idMeterList: number[]
    protected serialNbrList: number[]

    // Members End


    // Ctor

    constructor(private options: MeasureCollectionChartExtensionOptions, private userSession: UserSessionService) {
        super()
    }

    // Ctor End


    // IIMRChartExtension Implementation

    public get extensionName(): string { return 'MeasureCollectionChartExtension' }

    public clear(): void {
        if (this.chart != null) {
            this.chart.option('dataSource', [])
        }
    }

    public getChartOptions(): DevExpress.viz.charts.dxChartOptions {
        return this.chartExtension != null ? this.chartExtension.getOptions() : null
    }

    public getPieChartOptions(): DevExpress.viz.charts.dxPieChartOptions {
        return this.pieChartExension != null ? this.pieChartExension.getOptions() : null
    }

    public getDevExpressChartType(type: Enums.ChartType): string {
        return this.chartController.getMeasurementService().chartTypeToDevExpressType(type)
    }

    public initialize(chartController: IIMRChartExtensionController): JQueryPromise<void> {
        this.idLocationList = []
        this.idMeterList = []
        this.serialNbrList = []
        this.chartController = chartController

        this.registerToolbarItems()

        return this
            .getSeriesDataTypeInGroup()
            .then((seriesDataTypeInGroupList: OpDataTypeInGroup[]) => {
                this.seriesDataTypeInGroupList = seriesDataTypeInGroupList

                return this.getDataTypeFormatDictionary(this.seriesDataTypeInGroupList)
            })
            .then((dataTypeFormatter: DataTypeFormatter) => {
                this.dataTypeFormatter = dataTypeFormatter

                return this.getSeriesInfos(this.seriesDataTypeInGroupList)
            })
            .then((seriesInfos: SeriesInfo[]) => {
                const toInitialize: JQueryPromise<any>[] = []

                this.pieOnlySeriesInfos = _.filter(seriesInfos, (seriesInfo: SeriesInfo) => _.contains(MeasureCollectionChartExtension.PIE_CHART_TYPES, seriesInfo.type))
                this.noPieSeriesInfos = _.filter(seriesInfos, (seriesInfo: SeriesInfo) => !_.contains(MeasureCollectionChartExtension.PIE_CHART_TYPES, seriesInfo.type))

                if (_.size(this.pieOnlySeriesInfos) > 0) {
                    this.pieChartExension = new MeasureCollectionChartExtensionPieChart(this)

                    toInitialize.push(this.pieChartExension.initialize(chartController))
                }

                if (_.size(this.noPieSeriesInfos) > 0) {
                    this.chartExtension = new MeasureCollectionChartExtensionChart(this)

                    toInitialize.push(this.chartExtension.initialize(chartController))
                }

                return $.when(...toInitialize)
            })
            .fail((error: any) => {
                this.chartController.onError(error)
            })
    }

    public refresh(): JQueryPromise<void> {
        this.loadState()
        this.chartController.showLoading()

        return this
            .loadData(this.seriesDataTypeInGroupList)
            .then((measureCollection: MeasureCollection[]) => {
                const uniqueIdDataTypesFromData: number[] = _.unique(measureCollection.map((measureCollection: MeasureCollection) => measureCollection.idDataType))
                const seriesIdDataTypes: number[] = _.map(this.seriesDataTypeInGroupList, (dataTypeInGroup: OpDataTypeInGroup) => dataTypeInGroup.idDataType)
                const existingIdDataTypesInData: number[] = _.filter(seriesIdDataTypes, (idDataType: number) => _.contains(uniqueIdDataTypesFromData, idDataType))

                if (!_.isArray(this.visibleIdDataTypes)) {
                    this.visibleIdDataTypes = uniqueIdDataTypesFromData
                }

                const toRefresh: JQueryPromise<any>[] = []

                if (this.chartExtension != null) {
                    const existingSeriesInfos = _.filter(this.noPieSeriesInfos, (seriesInfo: SeriesInfo) => _.contains(existingIdDataTypesInData, seriesInfo.dataTypeInGroup.idDataType))

                    existingSeriesInfos.forEach((seriesInfo: SeriesInfo) => {
                        seriesInfo.visible = _.contains(this.visibleIdDataTypes, seriesInfo.dataTypeInGroup.idDataType)
                    })

                    toRefresh.push(this.chartExtension.refresh(measureCollection, existingSeriesInfos))
                }

                if (this.pieChartExension != null) {
                    const existingSeriesInfos = _.filter(this.pieOnlySeriesInfos, (seriesInfo: SeriesInfo) => _.contains(existingIdDataTypesInData, seriesInfo.dataTypeInGroup.idDataType))

                    existingSeriesInfos.forEach((seriesInfo: SeriesInfo) => {
                        seriesInfo.visible = _.contains(this.visibleIdDataTypes, seriesInfo.dataTypeInGroup.idDataType)
                    })

                    toRefresh.push(this.pieChartExension.refresh(measureCollection, existingSeriesInfos))
                }

                $.when(...toRefresh)
                    .always(() => {
                        _.size(measureCollection) == 0 ? this.chartController.showNoDataText() : this.chartController.clearOverlayText()

                        this.chartController.hideLoading()
                    })
            })
            .fail((error: any) => {
                this.chartController.onError(error)
            })
    }

    // IIMRChartExtension Implementation End


    // IMeasureCollectionChartExtension 

    // IMeasureCollectionChartExtension End


    //#region Chart

    public getUnitWrappedText(dataTypeInGroup: OpDataTypeInGroup): string {
        if (dataTypeInGroup != null && dataTypeInGroup.dataType != null && dataTypeInGroup.dataType.unit != null) {
            return `[${dataTypeInGroup.dataType.unit.toString()}]`
        }

        return TypeHelper.emptyString
    }

    //#endregion


    //#region Data

    private loadData(dataTypeInGroupList: OpDataTypeInGroup[]): DataProviderRequest<MeasureCollection[]> {
        const fromDate: Date = this.getFromDate()
        const toDate: Date = this.getToDate()
        const idDataTypes: number[] = dataTypeInGroupList.map((dataTypeInGroup: OpDataTypeInGroup) => dataTypeInGroup.idDataType)

        if (this.options != null && this.options.data != null && _.isFunction(this.options.data.customMeasurementFilter)) {
            const customMeasurementFilterResult: JQueryPromise<MeasureCollection[]> = this.options.data.customMeasurementFilter(this.idLocationList, this.serialNbrList, this.idMeterList, idDataTypes, fromDate, toDate)

            return customMeasurementFilterResult != null ? DataProviderRequest.fromJQuery(customMeasurementFilterResult) : DataProviderRequest.fromValue<MeasureCollection[]>([])
        }

        return this.chartController
            .getUserSession()
            .getDataProvider().Measurements
            .getMeasurementsFilter(this.idLocationList, this.serialNbrList, this.idMeterList, idDataTypes, fromDate, toDate)
    }

    //#endregion


    //#region DataTypes

    private getSeriesDataTypeInGroup(): JQueryPromise<OpDataTypeInGroup[]> {
        let dataTypeGroupCategory: Enums.SimaxDataTypeGroupCategory = null
        let simaxModuleName: Enums.SimaxModuleName = null

        if (this.options != null && this.options.dataTypes != null) {
            if (this.options.dataTypes.dataTypeGroupCategory != null && this.options.dataTypes.simaxModuleName != null) {
                dataTypeGroupCategory = this.options.dataTypes.dataTypeGroupCategory
                simaxModuleName = this.options.dataTypes.simaxModuleName

                return DataTypeGroupComponent.getDataTypeInGroupByModule(this.chartController.getUserSession().getDataProvider(), dataTypeGroupCategory, simaxModuleName)
            }

            if (_.isArray(this.options.dataTypes.dataTypeInGroup)) {
                return $.Deferred<OpDataTypeInGroup[]>().resolve(this.options.dataTypes.dataTypeInGroup).promise()
            }
        }

        return $.Deferred<OpDataTypeInGroup[]>().resolve([]).promise()
    }

    private getSeriesInfos(opDataTypeInGroupList: OpDataTypeInGroup[]): JQueryPromise<SeriesInfo[]> {
        const seriesInfoPromises: JQueryPromise<SeriesInfo>[] = _.map(opDataTypeInGroupList, (seriesDataTypeInGroup: OpDataTypeInGroup) => {
            return $.when<any>(
                this.chartController.getMeasurementService().getChartTypeByIdDataType(seriesDataTypeInGroup.idDataType),
                this.chartController.getMeasurementService().getSeriesColorByIdDataType(seriesDataTypeInGroup.idDataType)
            )
                .then((chartType: Enums.ChartType, chartColor: Enums.KnownColorsEnum) => {
                    return {
                        color: chartColor,
                        type: chartType,
                        dataTypeInGroup: seriesDataTypeInGroup,
                        visible: false,
                    }
                })
        })

        return $.when(...seriesInfoPromises).then((...result: SeriesInfo[]) => result)
    }

    //#endregion

    //#region Formatting

    public formatValue(value: any, idDataType: Enums.DataType, idDataTypeClass: Enums.DataTypeClass): string {
        return this.dataTypeFormatter.formatDisplayValue(value, {
            idDataType,
            idDataTypeClass,
        })
    }

    private getDataTypeFormatDictionary(dataTypes: ReadonlyArray<OpDataTypeInGroup>): JQueryPromise<DataTypeFormatter> {
        const idDataTypes: number[] = _.map(dataTypes, (dataType: OpDataTypeInGroup) => {
            return dataType.idDataType
        })

        return this
            .getService<DataTypeFormatService>(DATA_TYPE_FORMAT_SERVICE_NAME)
            .getDataTypeFormatter(idDataTypes)
    }

    //#endregion


    //#region  Toolbar

    private registerToolbarItems(): void {
        const toolbarItems: any[] = this.chartController.getToolbarItems()
        let dateFrom: Date = moment().subtract(1, 'month').toDate()
        let dateTo: Date = moment().toDate()
        let dateFromVisible: boolean = true
        let dateToVisible: boolean = true

        if (this.options.toolbar != null) {
            if (this.options.toolbar.dateFrom != null) {
                if (this.options.toolbar.dateFrom.value != null) {
                    dateFrom = this.options.toolbar.dateFrom.value
                }

                if (this.options.toolbar.dateFrom.visible != null) {
                    dateFromVisible = this.options.toolbar.dateFrom.visible
                }
            }

            if (this.options.toolbar.dateTo != null) {
                if (this.options.toolbar.dateTo.value != null) {
                    dateTo = this.options.toolbar.dateTo.value
                }

                if (this.options.toolbar.dateTo.visible != null) {
                    dateToVisible = this.options.toolbar.dateTo.visible
                }
            }
        }

        toolbarItems.unshift(
            {
                location: 'before',
                widget: 'dxDateBox',
                options: {
                    displayFormat: window['simax'].getDateFormat('DATE_FORMAT_DX'),
                    type: "date",
                    value: dateFrom,
                    visible: dateFromVisible,

                    onInitialized: (e: any) => this.fromDateBox = e.component,
                    onValueChanged: (e) => {
                        this.toDateBox.option('min', e.value)
                        this.refresh()
                    }
                }
            },
            {
                location: 'before',
                widget: 'dxDateBox',
                options: {
                    dateOutOfRangeMessage: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ValueIsOutOfRange),
                    displayFormat: window['simax'].getDateFormat('DATE_FORMAT_DX'),
                    type: "date",
                    min: dateFrom,
                    value: dateTo,
                    visible: dateToVisible,

                    onInitialized: (e: any) => this.toDateBox = e.component,
                    onValueChanged: () => this.refresh()
                }
            },
            {
                location: 'before',
                widget: 'dxButton',
                options: {
                    hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Refresh),
                    template: TemplateHelper.getIcon(ConfigHelper.Icon.REFRESH, this.userSession),

                    onClick: () => this.refresh()
                }
            }
        )
    }

    private getFromDate(): Date {
        if (this.fromDateBox != null) {
            const dateValue: any = this.fromDateBox.option('value')
            const date: Date = moment(dateValue).toDate()

            date.setHours(0, 0, 0, 0)

            return date
        }

        return null
    }

    private getToDate(): Date {
        if (this.toDateBox != null) {
            const dateValue: any = this.toDateBox.option('value')
            const date: Date = moment(dateValue).toDate()

            date.setHours(23, 59, 59, 999)

            return date
        }

        return null
    }

    //#endregion


    //#region  State

    private loadState(): void {
        const state: any = this.chartController.getExtensionState()

        if (state != null) {
            if (_.isArray(state.visibleIdDataTypes)) {
                this.visibleIdDataTypes = state.visibleIdDataTypes
            }
        }
    }

    //#endregion
}
